import type { StorageSummary } from "@corklog/shared";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { useParams } from "react-router";

import {
  ApiError,
  createShelf,
  createStorage,
  fetchStorages,
  setShelfTemperature,
  setStorageTemperature,
} from "../api.js";
import { useMayWrite } from "../cellar.js";
import { CellarHeading } from "../components/CellarHeading.js";
import {
  actionButton,
  control,
  fieldLabel,
  resting,
  Select,
} from "../components/controls.js";
import { Temperature } from "../components/Temperature.js";
import { useReload } from "../offline.js";

/**
 * The cabinets in a cellar and the shelves in them — the two levels location
 * stops at.
 *
 * A page of its own, away from the wine list: this is furniture, set up when
 * the cellar is and corrected rarely, and it has no business standing between
 * somebody and the bottles.
 *
 * Rows and hairlines rather than cards: the visual model is a shelf runner,
 * and the nesting on screen is the nesting in the cabinet. Each form sits at
 * the end of the thing it adds to, so a shelf is added where the shelves
 * already are rather than behind a button that opens somewhere else.
 */
type State =
  | { status: "loading" }
  | { status: "ready"; storages: StorageSummary[] }
  | { status: "failed" };

function AddShelf({
  cellarId,
  storage,
  onAdded,
}: {
  cellarId: string;
  storage: StorageSummary;
  onAdded: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  return (
    <form
      className="mt-3 flex items-center gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (name.trim() === "" || busy) return;
        setBusy(true);
        setError(null);
        void createShelf(cellarId, storage.id, {
          name,
          capacity: null,
          temperatureC: null,
        })
          .then(() => {
            setName("");
            onAdded();
          })
          .catch((cause: unknown) => {
            setError(
              cause instanceof ApiError && cause.status === 409
                ? t("cellar.shelfExists")
                : t("cellar.saveFailed"),
            );
          })
          .finally(() => setBusy(false));
      }}
    >
      <label className="sr-only" htmlFor={`shelf-${storage.id}`}>
        {t("cellar.shelfName")}
      </label>
      {/* The width is on the wrapper rather than on the field: `control`
          carries `w-full`, and two width utilities on one element are decided
          by the stylesheet's order instead of by this file. */}
      <div className="w-32">
        <input
          id={`shelf-${storage.id}`}
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder={t("cellar.shelfName")}
          className={`${control} ${resting}`}
        />
      </div>
      <button
        type="submit"
        disabled={busy || name.trim() === ""}
        className={actionButton}
      >
        {t("cellar.addShelf")}
      </button>
      {error !== null && (
        <span role="alert" className="text-past text-sm">
          {error}
        </span>
      )}
    </form>
  );
}

export function Cellar() {
  const { t } = useTranslation();
  const { cellarId = "" } = useParams();
  const [state, setState] = useState<State>({ status: "loading" });
  const [name, setName] = useState("");
  const [purpose, setPurpose] = useState<StorageSummary["purpose"]>("Ageing");
  const [error, setError] = useState<string | null>(null);
  const mayWrite = useMayWrite();

  const load = useCallback(() => {
    void fetchStorages(cellarId)
      .then((storages) => setState({ status: "ready", storages }))
      .catch(() => setState({ status: "failed" }));
  }, [cellarId]);

  useReload(load);

  return (
    <section>
      <CellarHeading cellarId={cellarId} current="storages" />

      {state.status === "loading" && (
        <p className="text-muted mt-6 text-sm">{t("loading")}</p>
      )}
      {state.status === "failed" && (
        <p role="alert" className="text-past mt-6 text-sm">
          {t("cellar.loadFailed")}
        </p>
      )}

      {state.status === "ready" && (
        <>
          {state.storages.length === 0 ? (
            <p className="text-muted mt-6 max-w-prose text-sm">{t("cellar.empty")}</p>
          ) : (
            <ul className="border-line mt-8 border-t">
              {state.storages.map((storage) => (
                <li key={storage.id} className="border-line border-b py-5">
                  <div className="flex items-baseline justify-between gap-4">
                    <h2 className="font-display text-lg">{storage.name}</h2>
                    <span className="text-muted text-sm">
                      {t(`storagePurpose.${storage.purpose}`)}
                    </span>
                  </div>

                  {/* What the cabinet is set to, on the cabinet's own row: it
                      is a property of the furniture, like its purpose, and not
                      of any bottle standing in it. */}
                  <div className="mt-3 flex items-center gap-3">
                    <span className="text-muted text-sm">{t("cellar.temperature")}</span>
                    <Temperature
                      label={t("cellar.temperatureOf", { name: storage.name })}
                      value={storage.temperatureC ?? null}
                      onSave={(next) =>
                        setStorageTemperature(cellarId, storage.id, next).then(load)
                      }
                    />
                  </div>

                  {storage.shelves.length === 0 ? (
                    <p className="text-muted mt-2 text-sm">{t("cellar.noShelves")}</p>
                  ) : (
                    // A row per shelf rather than the wrapped line of names it
                    // used to be, because each of them now carries something to
                    // set. A shelf that leaves the field empty is not claiming
                    // to match the cabinet — it is saying nothing, and the
                    // cabinet answers for it.
                    <ul className="mt-3 flex flex-col gap-2">
                      {storage.shelves.map((shelf) => (
                        <li
                          key={shelf.id}
                          className="flex items-center justify-between gap-4"
                        >
                          <span className="font-display text-ink text-sm">
                            {shelf.name}
                          </span>
                          <Temperature
                            label={t("cellar.temperatureOf", { name: shelf.name })}
                            value={shelf.temperatureC ?? null}
                            onSave={(next) =>
                              setShelfTemperature(
                                cellarId,
                                storage.id,
                                shelf.id,
                                next,
                              ).then(load)
                            }
                          />
                        </li>
                      ))}
                    </ul>
                  )}

                  {mayWrite === true && (
                    <AddShelf cellarId={cellarId} storage={storage} onAdded={load} />
                  )}
                </li>
              ))}
            </ul>
          )}

          {/* The furniture is the cellar's, and building more of it is the
              owner's act. A guest reads which cabinets there are and what they
              are set to — both of which say something about the wine standing
              in them — and is offered no way to add to them. */}
          {mayWrite === true && (
            <form
              className="border-line mt-8 flex flex-wrap items-end gap-4 border-t pt-6"
              onSubmit={(event) => {
                event.preventDefault();
                if (name.trim() === "") return;
                setError(null);
                void createStorage(cellarId, {
                  name,
                  purpose,
                  capacity: null,
                  temperatureC: null,
                })
                  .then(() => {
                    setName("");
                    load();
                  })
                  .catch((cause: unknown) => {
                    setError(
                      cause instanceof ApiError && cause.status === 409
                        ? t("cellar.storageExists")
                        : t("cellar.saveFailed"),
                    );
                  });
              }}
            >
              <div className="w-56">
                <label className={fieldLabel} htmlFor="storage-name">
                  {t("cellar.storageName")}
                </label>
                <input
                  id="storage-name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  className={`${control} ${resting} mt-1`}
                />
              </div>
              <div className="w-44">
                <label className={fieldLabel} htmlFor="storage-purpose">
                  {t("cellar.storagePurpose")}
                </label>
                <div className="mt-1">
                  <Select
                    id="storage-purpose"
                    value={purpose}
                    onChange={(next) => setPurpose(next as StorageSummary["purpose"])}
                  >
                    <option value="Serving">{t("storagePurpose.Serving")}</option>
                    <option value="Ageing">{t("storagePurpose.Ageing")}</option>
                    <option value="Other">{t("storagePurpose.Other")}</option>
                  </Select>
                </div>
              </div>
              <button
                type="submit"
                disabled={name.trim() === ""}
                className={actionButton}
              >
                {t("cellar.addStorage")}
              </button>
              {error !== null && (
                <span role="alert" className="text-past text-sm">
                  {error}
                </span>
              )}
            </form>
          )}
        </>
      )}
    </section>
  );
}
